/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {forwardRef, useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'

import {
    Flex,
    Heading,
    Button,
    Skeleton,
    Box,
    Text,
    VStack,
    Fade,
    useTheme,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useDerivedProduct} from '../../hooks/use-derived-product'
import {useMiniCartContext} from '../../contexts/mini-cart-context'
import {useBazarVoice} from '../../integrations/bazarvoice/hooks/use-bazarvoice'

// project components
import {ModalImageGallery as ImageGallery} from '../../components/modal-image-gallery/index'
import Breadcrumb from '@salesforce/retail-react-app/app/components/breadcrumb'
import withRegistration from '../with-registration'
import {Skeleton as ImageGallerySkeleton} from '@salesforce/retail-react-app/app/components/image-gallery'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {useCustomToast} from '../../contexts/custom-toast-context'
import DisplayPrice from '@salesforce/retail-react-app/app/components/display-price'
import Swatch from '../swatch-group/swatch'
import SwatchGroup from '../swatch-group'
import {getPriceData} from '@salesforce/retail-react-app/app/utils/product-utils'
import PromoCallout from '../product-tile/promo-callout'
import {AvailabilityBadge} from '../availability-badge'
import ActualFitMessage from '../actual-fit-message'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../utils/site-config'

const ProductViewHeader = ({
    name,
    currency,
    priceData,
    category,
    product,
    isProductPartOfBundle
}) => {
    const intl = useIntl()
    const config = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id
    const ENABLE_FINAL_SALE_LABEL = getSiteEnvVarValue(config.app, 'ENABLE_FINAL_SALE_LABEL', siteId)
    let enableFinalSaleLabel =
        ENABLE_FINAL_SALE_LABEL === true ||
        ENABLE_FINAL_SALE_LABEL === 'true' ||
        product?.c_isEGiftCard ||
        product?.c_isGiftCard

    return (
        <VStack mr={4} spacing={1} align="flex-start" marginBottom={[0, 0, 4, 0, 0]}>
            {category && (
                <Skeleton isLoaded={category} minWidth={64}>
                    <Breadcrumb categories={category} />
                </Skeleton>
            )}

            {/* Availability Badge */}
            {product?.c_limitedQuantity && (
                <Skeleton isLoaded>
                    <AvailabilityBadge
                        callout={intl.formatMessage({
                            id: 'cart.product_edit_modal.availability.badge',
                            defaultMessage: 'Few Items Left'
                        })}
                    />
                </Skeleton>
            )}

            {/* Title */}
            <Skeleton isLoaded={name}>
                <Heading
                    color="black"
                    fontFamily="AzoSans"
                    fontSize="md"
                    fontStyle="normal"
                    fontWeight="normal"
                >
                    {name}
                </Heading>
            </Skeleton>

            {!isProductPartOfBundle && (
                <>
                    <Skeleton isLoaded={priceData?.currentPrice}>
                        {priceData?.currentPrice && (
                            <DisplayPrice
                                currentPriceProps={{
                                    color: priceData?.isOnSale ? 'red.700' : 'black',
                                    fontSize: 'md',
                                    fontStyle: 'normal',
                                    fontWeight: priceData?.isOnSale ? '400' : '500',
                                    lineHeight: '6',
                                    mr: '10px'
                                }}
                                listPriceProps={{
                                    color: 'grey.500',
                                    fontSize: 'md',
                                    fontStyle: 'normal',
                                    fontWeight: '500',
                                    lineHeight: '6',
                                    textDecoration: priceData?.isOnSale ? 'line-through' : 'none'
                                }}
                                priceData={priceData}
                                currency={currency}
                            />
                        )}
                    </Skeleton>

                    {/* Final Sale */}
                    {product?.c_isFinalSale && enableFinalSaleLabel && (
                        <Skeleton isLoaded>
                            <Text
                                color="gray.700"
                                fontSize="md"
                                fontStyle="normal"
                                fontWeight="medium"
                                lineHeight="6"
                            >
                                {intl.formatMessage({
                                    id: 'cart.product_edit_modal.final.sale',
                                    defaultMessage: 'Final Sale'
                                })}
                            </Text>
                        </Skeleton>
                    )}

                    <Skeleton isLoaded={product}>
                        {product?.productPromotions && (
                            <PromoCallout
                                product={{
                                    ...product,
                                    productPromotions: product.productPromotions.filter(
                                        (promo) =>
                                            !promo?.promotionId?.includes('approaching-discount')
                                    )
                                }}
                            />
                        )}
                    </Skeleton>
                </>
            )}
        </VStack>
    )
}

ProductViewHeader.propTypes = {
    name: PropTypes.string,
    currency: PropTypes.string,
    category: PropTypes.array,
    priceData: PropTypes.object,
    product: PropTypes.object,
    isProductPartOfBundle: PropTypes.bool
}

const ButtonWithRegistration = withRegistration(Button)
const ButtonWithRegistrationForWishlist = withRegistration(Button, {isWishlistAction: true})

/**
 * Render a product detail view that includes name, image gallery, price,
 * variant selections, action buttons
 */

export const ModalProductView = forwardRef(
    (
        {
            product,
            category,
            showFullLink = false,
            imageSize = 'md',
            isWishlistLoading = false,
            addToCart,
            updateCart,
            addToWishlist,
            updateWishlist,
            isProductLoading,
            isProductPartOfSet = false,
            isProductPartOfBundle = false,
            childOfBundleQuantity = 0,
            childProductOrderability,
            setChildProductOrderability,
            isBasketLoading = false,
            onVariantSelected = () => {},
            validateOrderability = (variant, quantity, stockLevel) =>
                !isProductLoading && variant?.orderable && quantity > 0 && quantity <= stockLevel,
            showImageGallery = true,
            setSelectedBundleQuantity = () => {},
            selectedBundleParentQuantity = 1
        },
        ref
    ) => {
        const {currency: activeCurrency} = useCurrency()
        const {showCustomToast} = useCustomToast()
        const intl = useIntl()
        const location = useLocation()
        const {
            isOpen: isAddToCartModalOpen,
            onOpen: onAddToCartModalOpen,
            onClose: onAddToCartModalClose
        } = useMiniCartContext()
        const theme = useTheme()
        const [showOptionsMessage, toggleShowOptionsMessage] = useState(false)
        const {
            showLoading,
            showInventoryMessage,
            inventoryMessage,
            quantity,
            minOrderQuantity,
            setQuantity,
            variant,
            variationParams,
            variationAttributes,
            stockLevel,
            isOutOfStock,
            unfulfillable
        } = useDerivedProduct(product, isProductPartOfSet, isProductPartOfBundle)

        const previousQuantityRef = useRef(quantity)

        useEffect(() => {
            previousQuantityRef.current = quantity
        }, [quantity])

        const priceData = useMemo(() => {
            const previousQuantity = previousQuantityRef.current
            setQuantity(previousQuantity)
            return getPriceData(product, {previousQuantity})
        }, [product, quantity])

        const canAddToWishlist = !isProductLoading
        const isProductASet = product?.type.set
        const isProductABundle = product?.type.bundle
        const errorContainerRef = useRef(null)
        const isDesktop = useBreakpointValue({base: false, lg: true})
        const fitValue = product?.c_bvHowDoesItFitAverageRating
        const {reviews: bazarVoiceReviews} = useBazarVoice({pid: product?.id})
        const hasBazaarVoiceReviews = (bazarVoiceReviews?.Results?.length || 0) > 0

        const {disableButton, customInventoryMessage} = useMemo(() => {
            let shouldDisableButton = showInventoryMessage
            let currentInventoryMsg = ''
            if (
                !shouldDisableButton &&
                (isProductASet || isProductABundle) &&
                childProductOrderability
            ) {
                // if any of the children are not orderable, it will disable the add to cart button
                const unavailableChildProductKey = Object.keys(childProductOrderability).find(
                    (key) => {
                        return childProductOrderability[key].showInventoryMessage
                    }
                )
                shouldDisableButton = !!unavailableChildProductKey
                if (unavailableChildProductKey) {
                    const unavailableChildProduct =
                        childProductOrderability[unavailableChildProductKey]
                    if (unavailableChildProduct.unfulfillable) {
                        currentInventoryMsg = intl.formatMessage(
                            {
                                defaultMessage: 'Only {stockLevel} left for {productName}!',
                                id: 'use_product.message.inventory_remaining_for_product'
                            },
                            {
                                stockLevel: unavailableChildProduct.stockLevel,
                                productName: unavailableChildProduct.productName
                            }
                        )
                    }
                    if (unavailableChildProduct.isOutOfStock) {
                        currentInventoryMsg = intl.formatMessage(
                            {
                                defaultMessage: 'Out of stock for {productName}',
                                id: 'use_product.message.out_of_stock_for_product'
                            },
                            {productName: unavailableChildProduct.productName}
                        )
                    }
                }
            }
            return {disableButton: shouldDisableButton, customInventoryMessage: currentInventoryMsg}
        }, [showInventoryMessage, childProductOrderability])

        const validateAndShowError = (opts = {}) => {
            const {scrollErrorIntoView = true} = opts
            // Validate that all attributes are selected before proceeding.
            const hasValidSelection = validateOrderability(variant, quantity, stockLevel)
            const showError = !isProductASet && !isProductABundle && !hasValidSelection
            const scrollToError = showError && scrollErrorIntoView

            toggleShowOptionsMessage(showError)

            if (scrollToError) {
                errorContainerRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                })
            }

            return hasValidSelection
        }

        const renderActionButtons = () => {
            const buttons = []
            const buttonText = {
                update: intl.formatMessage({
                    defaultMessage: 'Update',
                    id: 'product_view.button.update'
                }),
                addToCart: intl.formatMessage({
                    defaultMessage: 'Add to Bag',
                    id: 'product_view.button.add_to_cart'
                }),
                addSetToCart: intl.formatMessage({
                    defaultMessage: 'Add Set to Cart',
                    id: 'product_view.button.add_set_to_cart'
                }),
                addBundleToCart: intl.formatMessage({
                    defaultMessage: 'Add Bundle to Cart',
                    id: 'product_view.button.add_bundle_to_cart'
                }),
                addToWishlist: intl.formatMessage({
                    defaultMessage: 'Move to Wishlist',
                    id: 'product_view.button.add_to_wishlist'
                }),
                addSetToWishlist: intl.formatMessage({
                    defaultMessage: 'Add Set to Wishlist',
                    id: 'product_view.button.add_set_to_wishlist'
                }),
                addBundleToWishlist: intl.formatMessage({
                    defaultMessage: 'Add Bundle to Wishlist',
                    id: 'product_view.button.add_bundle_to_wishlist'
                })
            }

            const showError = () => {
                showCustomToast({
                    message: intl.formatMessage(API_ERROR_MESSAGE)
                })
            }

            const handleCartItem = async () => {
                const hasValidSelection = validateAndShowError()

                if (!hasValidSelection) {
                    return null
                }

                if (!addToCart && !updateCart) return null
                if (updateCart) {
                    await updateCart(variant || product, quantity)
                    return
                }
                try {
                    const itemsAdded = await addToCart(variant, quantity)
                    // Open modal only when `addToCart` returns some data
                    // It's possible that the item has been added to cart, but we don't want to open the modal.
                    // See wishlist_primary_action for example.
                    if (itemsAdded) {
                        onAddToCartModalOpen({
                            product,
                            itemsAdded,
                            selectedQuantity: quantity
                        })
                    }
                } catch (e) {
                    showError()
                }
            }

            const handleWishlistItem = async () => {
                if (!updateWishlist && !addToWishlist) return null
                if (updateWishlist) {
                    updateWishlist(product, variant, quantity)
                    return
                }
                addToWishlist(product, variant, quantity)
            }

            // child product of bundles do not have add to cart button
            if ((addToCart || updateCart) && !isProductPartOfBundle) {
                buttons.push(
                    <Button
                        variant={'webPrimary'}
                        w={'100%'}
                        key="cart-button"
                        onClick={handleCartItem}
                        isDisabled={disableButton}
                        isLoading={isBasketLoading}
                    >
                        {updateCart
                            ? buttonText.update
                            : isProductASet
                            ? buttonText.addSetToCart
                            : isProductABundle
                            ? buttonText.addBundleToCart
                            : buttonText.addToCart}
                    </Button>
                )
            }

            // child product of bundles do not have add to wishlist button
            if ((addToWishlist || updateWishlist) && !isProductPartOfBundle) {
                buttons.push(
                    <ButtonWithRegistrationForWishlist
                        key="wishlist-button"
                        onClick={handleWishlistItem}
                        disabled={isWishlistLoading || !canAddToWishlist}
                        isLoading={isWishlistLoading}
                        width="100%"
                        variant="outline"
                        marginBottom={4}
                    >
                        {updateWishlist
                            ? buttonText.update
                            : isProductASet
                            ? buttonText.addSetToWishlist
                            : isProductABundle
                            ? buttonText.addBundleToWishlist
                            : buttonText.addToWishlist}
                    </ButtonWithRegistrationForWishlist>
                )
            }

            return buttons
        }

        // Bind the reference with our `scope` that includes the internal validate function for this component.
        // Other values can be added to this scope as required.
        if (typeof ref === 'function') {
            ref = ref.bind({validateOrderability: validateAndShowError})
        }

        // Set the quantity of bundle child in a product bundle to ensure availability messages appear
        if (
            isProductPartOfBundle &&
            quantity != selectedBundleParentQuantity * childOfBundleQuantity
        ) {
            setQuantity(selectedBundleParentQuantity * childOfBundleQuantity)
        }

        useEffect(() => {
            if (isAddToCartModalOpen) {
                onAddToCartModalClose()
            }
        }, [location.pathname])

        useEffect(() => {
            if (
                !isProductASet &&
                !isProductABundle &&
                validateOrderability(variant, quantity, stockLevel)
            ) {
                toggleShowOptionsMessage(false)
            }
        }, [variationParams])

        useEffect(() => {
            if (variant) {
                onVariantSelected(product, variant, quantity)
            }
        }, [variant?.productId, quantity])

        useEffect(() => {
            if (isProductPartOfBundle || isProductPartOfSet) {
                const key = product.itemId ?? product.id
                // when showInventoryMessage is true, it means child product is not orderable
                setChildProductOrderability((previousState) => ({
                    ...previousState,
                    [key]: {
                        showInventoryMessage,
                        isOutOfStock,
                        unfulfillable,
                        stockLevel,
                        productName: product?.name
                    }
                }))
            }
        }, [showInventoryMessage, inventoryMessage])

        return (
            <Flex direction={'column'} data-testid="product-view" ref={ref}>
                {/* Basic information etc. title, price, breadcrumb*/}
                {isDesktop && (
                    <Box display={['none', 'none', 'block', 'none']}>
                        <ProductViewHeader
                            name={product?.name}
                            product={product}
                            priceData={priceData}
                            currency={product?.currency || activeCurrency}
                            category={category}
                            isProductPartOfBundle={isProductPartOfBundle}
                        />
                    </Box>
                )}
                <Flex
                    gap={['20px', '20px', '20px', '50px']}
                    direction={['column', 'column', 'column', 'row']}
                >
                    {showImageGallery && (
                        <>
                            <Box>
                                {product ? (
                                    <>
                                        <ImageGallery
                                            size={imageSize}
                                            imageGroups={product.imageGroups}
                                            selectedVariationAttributes={variationParams}
                                            lazy={isProductPartOfSet || isProductPartOfBundle}
                                        />
                                        {!isDesktop && (
                                            <Box
                                                mt={'20px'}
                                                mb={0}
                                                display={['block', 'block', 'block', 'none']}
                                            >
                                                <ProductViewHeader
                                                    name={product?.name}
                                                    product={product}
                                                    priceData={priceData}
                                                    currency={product?.currency || activeCurrency}
                                                    category={category}
                                                    isProductPartOfBundle={isProductPartOfBundle}
                                                />
                                            </Box>
                                        )}
                                    </>
                                ) : (
                                    <ImageGallerySkeleton />
                                )}
                            </Box>
                        </>
                    )}

                    {/* Variations & Quantity Selector & CTA buttons */}
                    <VStack align="stretch" spacing={'20px'} flex={1}>
                        <Box display={['none', 'none', 'none', 'block']}>
                            <ProductViewHeader
                                name={product?.name}
                                product={product}
                                priceData={priceData}
                                currency={product?.currency || activeCurrency}
                                category={category}
                                isProductPartOfBundle={isProductPartOfBundle}
                            />
                        </Box>
                        <VStack align="stretch" spacing={0}>
                            {isProductPartOfBundle && (
                                <Box>
                                    <Text fontWeight="medium" fontSize="md" aria-label="price">
                                        <label>
                                            {intl.formatMessage({
                                                defaultMessage: 'Quantity',
                                                id: 'product_view.label.quantity'
                                            })}
                                            : {childOfBundleQuantity}
                                        </label>
                                    </Text>
                                </Box>
                            )}
                            {/*
                                Customize the skeletons shown for attributes to suit your needs. At the point
                                that we show the skeleton we do not know how many variations are selectable. So choose
                                a a skeleton that will meet most of your needs.
                            */}
                            {showLoading ? (
                                <>
                                    {/* First Attribute Skeleton */}
                                    <Skeleton height={6} width={32} />
                                    <Skeleton height={20} width={64} />

                                    {/* Second Attribute Skeleton */}
                                    <Skeleton height={6} width={32} />
                                    <Skeleton height={20} width={64} />
                                </>
                            ) : (
                                variationAttributes.map(({id, name, selectedValue, values}) => {
                                    const swatches = values.map(
                                        ({href, name, image, value, orderable}, index) => {
                                            const content = image ? (
                                                <Box
                                                    height="100%"
                                                    width="100%"
                                                    minWidth="32px"
                                                    backgroundRepeat="no-repeat"
                                                    backgroundSize="cover"
                                                    backgroundColor={name.toLowerCase()}
                                                    backgroundImage={`url(${
                                                        image.disBaseLink || image.link
                                                    })`}
                                                />
                                            ) : (
                                                <Box as="span" className="swatch-content">
                                                    {name}
                                                </Box>
                                            )
                                            const hasSelection = Boolean(selectedValue?.value)
                                            const isSelected = selectedValue?.value === value
                                            const isFirst = index === 0
                                            // To mimic the behavior of a native radio input, only
                                            // one swatch should receive tab focus; the rest can be
                                            // selected using arrow keys when the swatch group has
                                            // focus. The focused element is the selected option or
                                            // the first in the group, if no option is selected.
                                            // This is a slight difference, for simplicity, from the
                                            // native element, where the first element is focused on
                                            // `Tab` and the _last_ element is focused on `Shift+Tab`
                                            const isFocusable =
                                                isSelected || (!hasSelection && isFirst)
                                            return (
                                                <Swatch
                                                    key={value}
                                                    href={href}
                                                    disabled={!orderable}
                                                    value={value}
                                                    name={name}
                                                    variant={id === 'color' ? 'circle' : 'square'}
                                                    selected={isSelected}
                                                    isFocusable={isFocusable}
                                                    id={id}
                                                >
                                                    {content}
                                                </Swatch>
                                            )
                                        }
                                    )
                                    return (
                                        <React.Fragment>
                                            {id === 'size' &&
                                                fitValue &&
                                                hasBazaarVoiceReviews && (
                                                <Flex>
                                                    <ActualFitMessage
                                                        showIcon
                                                        fit={fitValue}
                                                        textProps={{fontSize: 'xs'}}
                                                    />
                                                </Flex>
                                            )}
                                            <SwatchGroup
                                                isSizeSwatch={id !== 'color'}
                                                displayValueOnly={['color', 'size'].includes(id)}
                                                key={id}
                                                value={selectedValue?.value}
                                                displayName={selectedValue?.name || ''}
                                                label={intl.formatMessage(
                                                    {
                                                        defaultMessage: ' ',
                                                        id: 'product_view.label.variant_type'
                                                    },
                                                    {variantType: name}
                                                )}
                                                id={id}
                                            >
                                                {swatches}
                                            </SwatchGroup>
                                        </React.Fragment>
                                    )
                                })
                            )}
                            <Box ref={errorContainerRef}>
                                {!showLoading && showOptionsMessage && (
                                    <Fade in={true}>
                                        <Text color="orange.600" fontWeight={600} marginBottom={8}>
                                            {intl.formatMessage({
                                                defaultMessage:
                                                    'Please select all your options above'
                                            })}
                                        </Text>
                                    </Fade>
                                )}
                            </Box>
                            {isProductASet && <p>{product?.shortDescription}</p>}
                        </VStack>

                        <Box>
                            {!showLoading && showInventoryMessage && !customInventoryMessage && (
                                <Fade in={true}>
                                    <Text color="orange.600" fontWeight={600} marginBottom={8}>
                                        {inventoryMessage}
                                    </Text>
                                </Fade>
                            )}
                            {!showLoading && customInventoryMessage && (
                                <Fade in={true}>
                                    <Text color="orange.600" fontWeight={600} marginBottom={8}>
                                        {customInventoryMessage}
                                    </Text>
                                </Fade>
                            )}
                            <Box display={'block'}>{renderActionButtons()}</Box>
                        </Box>
                    </VStack>
                </Flex>
            </Flex>
        )
    }
)

ModalProductView.displayName = 'ModalProductView'

ModalProductView.propTypes = {
    product: PropTypes.object,
    isProductPartOfSet: PropTypes.bool,
    isProductPartOfBundle: PropTypes.bool,
    childOfBundleQuantity: PropTypes.number,
    category: PropTypes.array,
    isProductLoading: PropTypes.bool,
    isBasketLoading: PropTypes.bool,
    isWishlistLoading: PropTypes.bool,
    addToCart: PropTypes.func,
    addToWishlist: PropTypes.func,
    updateCart: PropTypes.func,
    updateWishlist: PropTypes.func,
    showFullLink: PropTypes.bool,
    imageSize: PropTypes.oneOf(['sm', 'md']),
    childProductOrderability: PropTypes.object,
    setChildProductOrderability: PropTypes.func,
    onVariantSelected: PropTypes.func,
    validateOrderability: PropTypes.func,
    showImageGallery: PropTypes.bool,
    setSelectedBundleQuantity: PropTypes.func,
    selectedBundleParentQuantity: PropTypes.number
}
